import React, { Fragment, useRef } from 'react'
import PropTypes from 'prop-types'
import { Row, Col } from 'react-flexbox-grid'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

import { ExpandableBlocks, ContentBlock } from 'components/ContentBlock'
import { Form, DateTimePicker, WeekdayPicker, Checkbox, Text, TextArea } from 'components/forms'
import CashregisterView from 'components/CashRegisterTree'
import Button from 'components/Button'
import Divider from 'components/Divider'
import fetchedDataToForm from 'helpers/fetchedDataToForm'
import { notEmpty } from 'helpers/validators'
import { DATE_FORMAT } from 'config/consts'

const ItemDetails = ({ data = {}, isParentExpanded, onDataChange, itemType = 0 }) => {
	const allCRIds = useRef()
	const [t] = useTranslation()
	const isNewItem = !(data && data.id)
	const strippedData = fetchedDataToForm(data) || {}
	const formData = {
		weekDays: 127,
		...strippedData,
		type: itemType,
		dateInterval: strippedData.dateInterval || {}
	}
	const title = data && data.dateInterval ? data.dateInterval.from && data.dateInterval.to ?
		`${moment(data.dateInterval.from).format(DATE_FORMAT)} - ${moment(data.dateInterval.to).format(DATE_FORMAT)}` :
		t('product_section.strings.interval_not_selected') : t('common.strings.new')
	const validateFunc = notEmpty().validate
	const handleSubmit = vals => {
		const outVals = { ...vals }
		if(isNewItem && !vals.cashRegisterId) {
			outVals.cashRegisterId = allCRIds.current
		}
		onDataChange(outVals)
	}

	return(!(data && isParentExpanded) ? null :
		<ContentBlock label={title} related alwaysExpanded style={{ flexBasis: '100%' }}>
			<Form
				initialValues={formData}
				onSubmit={handleSubmit}
				reinitByModVer={false}
				smartSubmit={!isNewItem}
			>
				{({ formApi }) => (
					<>
						<ExpandableBlocks nested>
							<ContentBlock label={t('product_section.strings.general_information')} startExpanded>
								<Row>
									<Col xs>
										<div><Text field='type' hidden /></div>
										<div><Text field='id' hidden /></div>
										<DateTimePicker
											field='dateInterval'
											interval
											validate={({ from, to }) => validateFunc(from) || validateFunc(to)}
											validateOnChange={!isNewItem}
											validateOnBlur={false}
											label={{
												from: t('common.fields.from_date'),
												to: t('common.fields.to_date')
											}}
										/>
										<Checkbox field='replay' label={t('product_section.strings.repeat')} />
									</Col>
									<Col xs>
										<WeekdayPicker field='weekDays' label={t('product_section.fields.week_days')} />
									</Col>
									<Col xs>
										<TextArea field='description' label={t('product_section.strings.comment')} />
									</Col>
								</Row>
							</ContentBlock>
							<ContentBlock label={t('common.fields.cash_register')}>
								<div><Text field='cashRegisterId' hidden /></div>
								<CashregisterView
									key={formData?.cashRegisterId?.length}
									activeNodesArr={formData?.cashRegisterId}
									onChange={val => {
										formApi.setValue('cashRegisterId', val)
										if(!isNewItem) {
											formApi.submitForm()
										}
									}}
									startAllActive={isNewItem}
									allValsRef={allCRIds}
									inverted
								/>
							</ContentBlock>
						</ExpandableBlocks>
						{isNewItem &&
							<Fragment>
								<Divider />
								<Button text={t('common.buttons.add')} type='submit' status='primary' />
							</Fragment>
						}
					</>
				)}
			</Form>
		</ContentBlock>
	)
}

ItemDetails.propTypes = {
	data: PropTypes.object,
	isParentExpanded: PropTypes.bool,
	onDataChange: PropTypes.func.isRequired,
	itemType: PropTypes.number.isRequired
}

export default ItemDetails
